import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, ListChecks } from 'lucide-react';
import { useAdminBooking, useUpdateAdminBookingStatus } from '../../services/adminBookingService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentApi } from '../../services/paymentService';
import PaymentHistoryModal from '../../components/client/PaymentHistoryModal';
import AdminStatusUpdateModal from '../../components/admin/StatusUpdateModal';
import EventLogs from '../../components/shared/EventLogs';
import { useEventLogs } from '../../hooks/useEventLogs';
import {
  BookingReference,
  EventDetails,
  ServicePackageDetails,
  ClientReview
} from '../../components/vendor/booking';
import {
  AdminBookingHeader,
  AdminClientInfo,
  AdminVendorInfo,
  AdminPaymentSummary
} from '../../components/admin/booking';
import { getAdminInvoice } from '../../components/admin/booking/AdminInvoice';
import BookingChatPanel from '../../components/booking/BookingChatPanel';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';
import { Button } from '../../components/shared';

const AdminBookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);

  const { data: response, isLoading, error, refetch } = useAdminBooking(id);
  const booking = response?.data || response;
  
  // Fetch event logs
  const { eventLogs, isLoading: isLoadingEventLogs } = useEventLogs(id);

  // Fetch payments for this booking
  const { data: paymentsResponse, isLoading: isLoadingPayments } = useQuery({
    queryKey: ['payments', id],
    queryFn: () => paymentApi.getPaymentByBooking(id),
    enabled: !!id && !!booking,
    select: (response) => {
      // Handle different response structures
      if (Array.isArray(response)) {
        return response;
      }
      if (response?.data) {
        return Array.isArray(response.data) ? response.data : [];
      }
      return [];
    }
  });

  const payments = paymentsResponse || [];

  // Debug logging
  React.useEffect(() => {
    if (payments.length > 0) {
      console.log('Payments fetched:', payments);
    } else if (!isLoadingPayments && id) {
      console.log('No payments found for booking:', id);
    }
  }, [payments, isLoadingPayments, id]);

  // Update booking status mutation
  const updateStatusMutation = useUpdateAdminBookingStatus({
    onSuccess: () => {
      queryClient.invalidateQueries(['adminBooking', id]);
      setShowStatusModal(false);
      refetch();
    },
    onError: (error) => {
      console.error('Failed to update booking status:', error);
    }
  });

  const handleStatusUpdate = (newStatus, notes) => {
    if (newStatus && newStatus !== booking?.status) {
      updateStatusMutation.mutate({
        id,
        status: newStatus,
        notes: notes || null
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) {
        return timeString;
      }
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return timeString;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
      confirmed: 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300',
      in_progress: 'bg-teal-50 text-teal-800 dark:bg-teal-950/40 dark:text-teal-300',
      completed: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
      cancelled: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
      no_show: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
    };
    return colors[status] || colors.pending;
  };

  const formatStatusText = (status) => {
    return status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Pending';
  };

  // Use AdminInvoice utility for invoice generation
  const { handleDownloadInvoice } = getAdminInvoice(booking, payments);

  const SkeletonLoader = () => (
    <div className="space-y-5 animate-pulse">
      <div className="h-8 w-48 bg-gray-200 dark:bg-gray-800 rounded-xl" />
      <div className="h-24 rounded-2xl bg-gray-200 dark:bg-gray-800" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 h-64 rounded-2xl bg-gray-200 dark:bg-gray-800" />
        <div className="h-64 rounded-2xl bg-gray-200 dark:bg-gray-800" />
      </div>
    </div>
  );

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (error || !booking) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Booking Not Found</h3>
          <p className="text-red-600 dark:text-red-300 text-sm mb-6">
            {error?.response?.data?.message || 'The booking you are looking for does not exist.'}
          </p>
          <Button
            className="!rounded-xl"
            onClick={() => navigate('/admin/bookings')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Bookings
          </Button>
        </div>
      </div>
    );
  }

  const client = booking.client;
  const vendor = booking.vendor;
  const service = booking.vendor_service || booking.vendorService;
  const packageData = booking.vendor_service_package || booking.vendorServicePackage;

  // Calculate payment totals
  const completedPayments = payments.filter(p => p.status === 'completed');
  const totalPaid = completedPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const depositPaid = completedPayments.filter(p => p.payment_type === 'deposit')
    .reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const fullPaymentPaid = completedPayments.filter(p => p.payment_type === 'full_payment').length > 0;

  // Check for reviews
  const hasReview = booking.reviews && booking.reviews.length > 0;
  const review = hasReview ? booking.reviews[0] : null;

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title={`Booking ${booking.booking_reference || ''}`}
        subtitle="Review booking details, payments, chat, and status."
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.bookingDetail.title}
        steps={ADMIN_PAGE_GUIDES.bookingDetail.steps}
        icon={ListChecks}
      />

      <AdminBookingHeader
        onBack={() => navigate('/admin/bookings')}
        status={booking.status}
        statusColor={getStatusColor(booking.status)}
        statusText={formatStatusText(booking.status)}
        onUpdateStatus={() => {
          if (booking.status !== 'completed') {
            setShowStatusModal(true);
          }
        }}
      />

      <BookingReference
        bookingReference={booking.booking_reference}
        createdAt={booking.created_at}
        formatDate={formatDate}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
        <div className="lg:col-span-2 space-y-3 sm:space-y-4">
          <EventDetails
            booking={booking}
            formatDate={formatDate}
            formatTime={formatTime}
          />

          <BookingChatPanel bookingId={id} isAdmin className="min-h-[320px]" />

          {eventLogs && eventLogs.length > 0 && (
            <EventLogs eventLogs={eventLogs} isLoading={isLoadingEventLogs} />
          )}

          <ServicePackageDetails
            service={service}
            packageData={packageData}
          />

          <ClientReview review={review} />
        </div>

        <div className="space-y-3 sm:space-y-4">
          <AdminClientInfo client={client} />
          <AdminVendorInfo vendor={vendor} />
          <AdminPaymentSummary
            booking={booking}
            totalPaid={totalPaid}
            depositPaid={depositPaid}
            fullPaymentPaid={fullPaymentPaid}
            payments={payments}
            onDownloadInvoice={handleDownloadInvoice}
            onViewPaymentHistory={() => setShowPaymentHistoryModal(true)}
          />
        </div>
      </div>

      <AdminStatusUpdateModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        currentStatus={booking?.status}
        onUpdate={handleStatusUpdate}
        isUpdating={updateStatusMutation.isPending}
      />

      <PaymentHistoryModal
        isOpen={showPaymentHistoryModal}
        onClose={() => setShowPaymentHistoryModal(false)}
        payments={payments}
      />
    </div>
  );
};

export default AdminBookingDetails;
